import ActionButton from "@/components/buttons/ActionButton";
import HiddenInput from "@/components/form/components/HiddenInput";
import LabelWithTooltip from "@/components/form/labels/LabelWithTooltip";
import DeleteIcon from "@/components/icons/DeleteIcon";
import { ItemCreatedUpdatedInfoView } from "@/components/views/ItemCreatedUpdatedInfoView";
import { datePickerFormItemProps, datePickerStandardProps, selectStandardProps } from "@/utils/componentUtils";
import { resolveFormValidationError, setErrorsToForm } from "@/utils/formUtils";
import { dateToIsoDateString, isDefined, toDate } from "@/utils/utils";
import { validations } from "@/utils/validationUtils";
import { Checkbox, Col, DatePicker, Divider, Form, Input, Modal, Row, Select } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useCreateAgentCompetence, useUpdateAgentCompetence } from "../../../../common/api/queries";
import { ModalSizes, rowGutter } from "../../../../common/constants";
import type { UUID } from "../../../../typings/global";
import { AgentCompetenceLevel, AgentCompetenceProvider, AgentCompetenceType, FinancialSector } from "../../enums";
import { AgentCompetence, AgentCompetenceList, CreateUpdateAgentCompetence } from "../../types";
import { formatAgentAggregatedName } from "../../utils";

interface Props {
  open: boolean;
  agentId?: UUID;
  competence?: AgentCompetence | AgentCompetenceList;
  onFormCancel: () => void;
}

export const AgentCompetenceForm = ({ open, agentId, competence, onFormCancel }: Props) => {
  const { t } = useTranslation();

  const [form] = Form.useForm<CreateUpdateAgentCompetence>();
  const type = Form.useWatch("type", form);
  const intervals = Form.useWatch("intervals", form);

  const createAgentCompetence = useCreateAgentCompetence();
  const updateAgentCompetence = useUpdateAgentCompetence();

  useEffect(() => {
    if (!open) {
      return;
    }

    if (competence) {
      form.setFieldsValue({ ...competence });
    } else {
      form.setFieldsValue({ intervals: [{ processed: false }] });
    }
  }, [open, competence, form]);

  const handleIntervalEndDateDependingField = (fieldIndex?: number) => {
    const { type, intervals } = form.getFieldsValue(["type", "intervals"]) as CreateUpdateAgentCompetence;

    if (type && intervals) {
      form.setFieldsValue({
        intervals: intervals.map((i) => {
          let endDate: Dayjs | undefined;
          switch (type) {
            case AgentCompetenceType.TEST:
              endDate = i.startDate ? toDate(i.startDate)?.add(4, "year").subtract(1, "day") : undefined;
              break;
            case AgentCompetenceType.TRAINING:
              endDate = i.startDate ? toDate(i.startDate)?.add(1, "year").subtract(1, "day") : undefined;
              break;
            case AgentCompetenceType.OTHER:
              endDate = isDefined(fieldIndex) ? toDate(i.endDate) : undefined;
              break;
          }
          return { ...i, endDate: endDate ? dateToIsoDateString(endDate) : undefined };
        }),
      });
    }
  };

  const handleFormSubmit = () => {
    if (agentId) {
      form
        .validateFields()
        .then((values) => {
          if (competence) {
            updateAgentCompetence.mutate(
              { id1: agentId, id2: competence.id, object: values },
              {
                onSuccess: onFormCancel,
                onError: (error) => setErrorsToForm(form, "agent.competence.attrs", error.violations),
              },
            );
          } else {
            createAgentCompetence.mutate(
              { id: agentId, object: values },
              {
                onSuccess: onFormCancel,
                onError: (error) => setErrorsToForm(form, "agent.competence.attrs", error.violations),
              },
            );
          }
        })
        .catch(resolveFormValidationError);
    }
  };

  return (
    <Modal
      width={ModalSizes.MEDIUM}
      open={open}
      title={
        competence
          ? t("agent.competence.titles.update") +
            ((competence as AgentCompetenceList).agent
              ? ` | ${formatAgentAggregatedName((competence as AgentCompetenceList).agent)}`
              : "")
          : t("agent.competence.titles.create")
      }
      cancelText={t("common.cancel")}
      okText={t("common.save")}
      confirmLoading={createAgentCompetence.isPending || updateAgentCompetence.isPending}
      afterClose={() => form.resetFields()}
      onOk={handleFormSubmit}
      onCancel={onFormCancel}
      mask={{ closable: false }}
    >
      <ItemCreatedUpdatedInfoView className="margin-bottom-small" item={competence} />

      <Form form={form} layout="vertical" name="agentCompetenceForm">
        <HiddenInput name="optimisticLockVersion" />

        <Row gutter={rowGutter}>
          <Col span={8}>
            <Form.Item
              name="type"
              label={t("agent.competence.enums.competenceType._label")}
              rules={[validations.notNull]}
            >
              <Select
                {...selectStandardProps}
                options={Object.keys(AgentCompetenceType).map((type) => ({
                  value: type,
                  label: t("agent.competence.enums.competenceType." + type),
                }))}
                onChange={() => handleIntervalEndDateDependingField(undefined)}
              />
            </Form.Item>
          </Col>
        </Row>

        <>
          {(type === AgentCompetenceType.TEST || type === AgentCompetenceType.TRAINING) && (
            <Row gutter={rowGutter}>
              <Col span={8}>
                <Form.Item name="sector" label={t("agent.enums.financialSector._label")} rules={[validations.notNull]}>
                  <Select
                    {...selectStandardProps}
                    options={Object.keys(FinancialSector).map((sector) => ({
                      value: sector,
                      label: t("agent.enums.financialSector." + sector),
                    }))}
                  />
                </Form.Item>
              </Col>

              <Col span={8}>
                <Form.Item
                  name="level"
                  label={t("agent.competence.enums.competenceLevel._label")}
                  rules={[validations.notNull]}
                >
                  <Select
                    {...selectStandardProps}
                    options={Object.keys(AgentCompetenceLevel).map((level) => ({
                      value: level,
                      label: t("agent.competence.enums.competenceLevel." + level),
                    }))}
                  />
                </Form.Item>
              </Col>
            </Row>
          )}

          {type === AgentCompetenceType.OTHER && (
            <Row gutter={rowGutter}>
              <Col span={24}>
                <Form.Item
                  name="label"
                  label={t("agent.competence.attrs.label")}
                  rules={[validations.notBlank, validations.size(1, 255)]}
                >
                  <Input />
                </Form.Item>
              </Col>
            </Row>
          )}

          <Row gutter={rowGutter}>
            <Col span={24}>
              <Form.Item name="note" label={t("agent.competence.attrs.note")} rules={[validations.size(1, 8192)]}>
                <Input.TextArea autoSize={{ minRows: 2, maxRows: 5 }} />
              </Form.Item>
            </Col>
          </Row>

          <Divider>{t("agent.competence.attrs.intervals._label")}</Divider>

          <Form.List name={["intervals"]} initialValue={[{}]}>
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, ...field }) => (
                  <Row gutter={rowGutter} key={key}>
                    <Col span={22}>
                      <HiddenInput name={[field.name, "id"]} />
                      <HiddenInput name={[field.name, "optimisticLockVersion"]} />

                      <Row gutter={rowGutter}>
                        <Col span={6}>
                          <Form.Item
                            {...field}
                            name={[field.name, "startDate"]}
                            label={t("agent.competence.attrs.intervals.startDate")}
                            rules={[validations.notNull]}
                            {...datePickerFormItemProps}
                          >
                            <DatePicker
                              {...datePickerStandardProps}
                              onChange={() => handleIntervalEndDateDependingField(field.name)}
                            />
                          </Form.Item>
                        </Col>

                        <Col span={6}>
                          <Form.Item
                            {...field}
                            name={[field.name, "endDate"]}
                            label={t("agent.competence.attrs.intervals.endDate")}
                            rules={[
                              validations.notNull,
                              validations.notBefore(
                                intervals?.[field.name]?.startDate,
                                t("agent.competence.attrs.intervals.startDate"),
                              ),
                            ]}
                            {...datePickerFormItemProps}
                          >
                            <DatePicker
                              {...datePickerStandardProps}
                              disabled={type === AgentCompetenceType.TEST || type === AgentCompetenceType.TRAINING}
                              minDate={
                                intervals?.[field.name]?.startDate ? dayjs(intervals[field.name]?.startDate) : undefined
                              }
                            />
                          </Form.Item>
                        </Col>

                        <Col span={6}>
                          <Form.Item
                            {...field}
                            name={[field.name, "processed"]}
                            className="form-item-without-label"
                            valuePropName="checked"
                            rules={[validations.none]}
                            initialValue={false}
                          >
                            <Checkbox>
                              <LabelWithTooltip
                                tooltip={t("agent.competence.helpers.processedIntervalDesc")}
                                label={t("agent.competence.attrs.intervals.processed")}
                              />
                            </Checkbox>
                          </Form.Item>
                        </Col>

                        {(type === AgentCompetenceType.TEST || type === AgentCompetenceType.TRAINING) && (
                          <Col span={6}>
                            <Form.Item
                              {...field}
                              name={[field.name, "provider"]}
                              label={t("agent.competence.enums.competenceProvider._label")}
                              rules={[validations.none]}
                            >
                              <Select
                                {...selectStandardProps}
                                allowClear
                                options={Object.keys(AgentCompetenceProvider).map((provider) => ({
                                  value: provider,
                                  label: t("agent.competence.enums.competenceProvider." + provider),
                                }))}
                              />
                            </Form.Item>
                          </Col>
                        )}
                      </Row>
                    </Col>

                    <Col span={2}>{fields.length > 1 && <DeleteIcon onClick={() => remove(field.name)} />}</Col>
                  </Row>
                ))}

                <ActionButton icon="plus" className="margin-top-small" onClick={() => add()}>
                  {t("agent.competence.actions.addInterval")}
                </ActionButton>
              </>
            )}
          </Form.List>
        </>
      </Form>
    </Modal>
  );
};
